import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './Projects.css';

interface Project {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  fullDescription: string;
  features: string[];
  technologies: string[];
  image: string;
  category: 'mobile' | 'web' | 'security' | 'ai' | 'desktop';
  status: 'completed' | 'in-progress';
  links?: {
    playStore?: string;
    appStore?: string;
    demo?: string;
    website?: string;
    fdroid?: string;
  };
}

const Projects: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const projects: Project[] = [
    {
      id: 1,
      title: "Aplicación Android de Seguridad",
      subtitle: "Borrado Remoto",
      description: "🚀 Remote Wipe App – Gestión y Seguridad para Dispositivos Android. Protege y administra tus dispositivos Android en tiempo real. Ideal para usuarios y empresas que requieren control total. 💼🔒",
      fullDescription: "Es un FORK de SESSION enfocado en la privacidad y seguridad, resolviendo posibles vectores de ataque en tu código. Todo el ecosistema funciona igual que SESSION, pero con diferencias claves:",
      features: [
        "🔥 Borrado Remoto de Dispositivos: Elimina datos a distancia en caso de pérdida o robo",
        "🔑 Intentos de Desbloqueo Fallidos: Configura el borrado automático tras varios intentos fallidos",
        "📡 Notificaciones en Tiempo Real: Monitoriza y actualiza el estado del dispositivo desde el servidor",
        "🛡️ Gestión de Licencias: Registro y administración segura de licencias",
        "🖥️ Compatibilidad con MDM: Aprovecha el modo \"Owner\" de Android para un control total"
      ],
      technologies: ["Kotlin", "Jetpack Compose", "DevicePolicyManager", "Node.js", "WebSocket", "PostgreSQL", "JWT"],
      image: "/images/projects/proyecto1.png",
      category: "security",
      status: "completed"
    },
    {
      id: 2,
      title: "Escáner Facial & Detector de Atributos",
      subtitle: "ANDROID",
      description: "🚀 EscánerFacial Belleza – ¡Realza tu rostro con IA! 🤳 Descubre tu mejor versión con nuestro escáner avanzado que analiza más de 40 puntos clave de tu rostro.",
      fullDescription: "Obtén consejos personalizados para resaltar tus rasgos más atractivos, mide la simetría facial de manera precisa y diviértete comparándote con tus celebridades favoritas.",
      features: [
        "Análisis de Belleza Facial: Genera gráficos detallados que destacan proporciones de ojos, pómulos y labios",
        "Escaneo Facial Simétrico: Verifica el nivel de simetría en ambos lados del rostro",
        "Comparador de Famosos: Identifica a qué celebridad te pareces más con algoritmos de similitud",
        "Recomendaciones personalizadas para equilibrar tus facciones"
      ],
      technologies: ["Kotlin", "Java", "MediaPipe", "OpenCV", "Machine Learning"],
      image: "/images/projects/proyecto2.png",
      category: "ai",
      status: "completed",
      links: {
        playStore: "https://play.google.com/store/apps/details?id=com.marquez.detection&hl=es"
      }
    },
    {
      id: 3,
      title: "SecureYourChats",
      subtitle: "NewOverlayTechnology",
      description: "Encripta o desencripta texto y archivos con algoritmos potentes (AES, DES, CAMELLIA, ChaCha20-Poly1305, XChaCha20-Poly1305, Aegis256).",
      fullDescription: "Interfaz intuitiva para máxima seguridad y privacidad en tus comunicaciones digitales.",
      features: [
        "📝 Text Encryption/Decryption: Interfaz intuitiva para ingresar texto, elegir algoritmo y ver resultados",
        "🔑 Compartir/Importar Claves vía QR: Comparte tu clave o importa la de otros fácilmente",
        "📁 File Encryption/Decryption: Selecciona archivos y aplica los mismos algoritmos",
        "🗝️ Built-In Key Manager: Añade, borra o genera claves aleatorias",
        "♿ Servicio de Accesibilidad (Opcional): Detecta texto seleccionado o copiado"
      ],
      technologies: ["Android", "AES", "DES", "CAMELLIA", "ChaCha20-Poly1305", "EncryptedSharedPreferences"],
      image: "/images/projects/proyecto3.png",
      category: "security",
      status: "completed"
    },
    {
      id: 4,
      title: "ThreadScoopOnionChat",
      subtitle: "",
      description: "🚀 ThreatScoopOnionChat – ¡Protege tus conversaciones con seguridad avanzada! 🔒 Es un FORK de SESSION enfocado en la privacidad y seguridad.",
      fullDescription: "Resuelve posibles vectores de ataque en tu código. Todo el ecosistema funciona igual que SESSION, pero con diferencias claves.",
      features: [
        "Claves de 256 bits (en lugar de 128 bits en SESSION)",
        "25 Palabras Mnemónicas (vs. 12 en SESSION)",
        "Capa extra de cifrado en todo el ecosistema",
        "Eliminación de GoogleServices en el proyecto",
        "Función ExtraSecurity: el núcleo de ThreatScoopOnionChat"
      ],
      technologies: ["Android", "Signal Protocol", "Encryption", "Privacy", "Session Fork"],
      image: "/images/projects/proyecto4.png",
      category: "security",
      status: "completed",
      links: {
        fdroid: "https://f-droid.org/packages/com.bwt.threadscooponionchatcom/"
      }
    },
    {
      id: 5,
      title: "SecureChatsKeyboard",
      subtitle: "",
      description: "🚀 SecureChats KEYBOARD – ¡Cifrado de extremo a extremo (E2EE) para cualquier app! 🔒 Es un teclado de Android que integra el Protocolo Signal (X3DH y Double Ratchet).",
      fullDescription: "Ahora cifrado post-cuántico Kyber, sin depender de un servidor. Comunícate de forma segura e independiente, incluso si tu mensajería favorita no soporta E2EE.",
      features: [
        "Envía y recibe mensajes cifrados en cualquier app mediante un campo de texto sobre el teclado",
        "Decodifica mensajes usando el portapapeles",
        "Administra contactos directamente en el teclado",
        "Modo Raw o Modo Cuento de Hadas (Fairytale) para disfrazar la data",
        "Basado en el Protocolo Signal (X3DH, Double Ratchet) con AES-256-CBC y SHA-256"
      ],
      technologies: ["Android", "Signal Protocol", "X3DH", "Double Ratchet", "Kyber", "E2EE"],
      image: "/images/projects/proyecto5.png",
      category: "security",
      status: "completed",
      links: {
        playStore: "https://play.google.com/store/apps/details?id=com.bwt.securechats&hl=es"
      }
    },
    {
      id: 6,
      title: "PatronesCostura & Asistente",
      subtitle: "",
      description: "🚀 ¡Bienvenido(a) a la mejor aliada para tu pasión por la costura! 🧵✨ Nuestra aplicación combina patrones de costura personalizados, asistente de costura inteligente y herramientas prácticas.",
      fullDescription: "Para llevar tus creaciones a un nivel profesional, seas principiante o experto(a) en diseño de modas.",
      features: [
        "Patrones Básicos y Avanzados: Accede a una amplia selección de patrones (blusas, faldas, pantalones, mangas, etc.)",
        "Generador de PDFs: Crea, combina e imprime tus patrones en formato PDF desde la app",
        "Gestor de Perfiles de Medidas: Guarda las medidas de varias personas",
        "Vista Previa y Personalización: Observa cada pieza antes de imprimir",
        "Asistente de Costura Integrado 🤖: Responde dudas sobre técnicas, telas, cortes"
      ],
      technologies: ["Flutter", "PDF Generation", "AI Assistant", "Pattern Design"],
      image: "/images/projects/proyecto6.png",
      category: "mobile",
      status: "completed"
    },
    {
      id: 7,
      title: "CogniTest",
      subtitle: "",
      description: "🚀 CogniTest – ¡Ejercita tu mente de forma divertida y accesible! 🧠✨ Pon a prueba tu velocidad de reacción, memoria y reflejos con una serie de desafíos.",
      fullDescription: "Diseñados para mantener tu cerebro ágil. Compara tus resultados con usuarios de todo el mundo y sigue tu progreso en el tiempo.",
      features: [
        "Tiempo de Reacción",
        "Velocidad de Escritura",
        "Chimp Test",
        "Memoria Numérica",
        "Test de Audición",
        "Memoria Verbal",
        "Comparaciones Globales: Descubre en qué porcentaje te ubicas respecto a otros participantes"
      ],
      technologies: ["Flutter", "Kotlin", "Cognitive Testing", "Analytics"],
      image: "/images/projects/proyecto7.png",
      category: "mobile",
      status: "completed",
      links: {
        playStore: "https://play.google.com/store/apps/details?id=com.marquez.cognitivetrain&hl=es"
      }
    },
    {
      id: 8,
      title: "FrozenBalls (REBRANDING)",
      subtitle: "",
      description: "🔥❄️ ¡Bienvenido a Frozen Balls! El juego que fusiona hielo y fuego para ofrecer una experiencia única y desafiante.",
      fullDescription: "Aunque su nombre evoque frescura, prepárate para enfrentar escenarios infernales llenos de acción y adrenalina.",
      features: [
        "Mecánica Clásica y Adictiva: Conecta 3 o más burbujas para despejar el tablero",
        "Ambiente Infernal Único: Un contraste visual impactante del frío al calor del infierno",
        "Multijugador (2 Modos): Disfruta en solitario o reta a un amigo en partidas de 2 jugadores",
        "Diseño Moderno y Fluido: Interfaz intuitiva y jugabilidad pulida",
        "Gráficos HD y personajes renovados, mezclando lo retro con elementos modernos"
      ],
      technologies: ["Java", "Android Game Development", "OpenGL"],
      image: "/images/projects/proyecto8.png",
      category: "mobile",
      status: "completed",
      links: {
        playStore: "https://play.google.com/store/apps/details?id=com.bwtn.frozenbubble&hl=es"
      }
    },
    {
      id: 9,
      title: "SLEEPINESS Detector",
      subtitle: "",
      description: "🚗 SLEEPINESS Detector – ¡Porque tu seguridad es lo primero! 🛡️ Una aplicación que puede salvar vidas, diseñada para mantener seguros a los conductores durante sus viajes.",
      fullDescription: "Detecta señales tempranas de fatiga y previene accidentes antes de que ocurran. Tu copiloto digital que nunca duerme.",
      features: [
        "👁️ Detección Avanzada: Monitoreo en tiempo real del estado de los ojos del conductor",
        "🎯 Precisión y Velocidad: Tecnología Google ML Kit para detección facial precisa",
        "🔔 Sistema de Alerta: Alarma sonora potente y efectiva tipo bocina",
        "🛡️ Privacidad Primero: No almacena imágenes ni datos personales",
        "Procesamiento 100% local en el dispositivo"
      ],
      technologies: ["Flutter", "Google ML Kit", "Computer Vision", "Real-time Processing"],
      image: "/images/projects/proyecto9.png",
      category: "ai",
      status: "completed",
      links: {
        playStore: "https://play.google.com/store/apps/details?id=com.bwt.driverassistant&hl=es"
      }
    },
    {
      id: 10,
      title: "Gestión de Gimnasios",
      subtitle: "ElevenSport",
      description: "🏋️‍♂️ ElevenSport – ¡Sistema integral para la gestión moderna de gimnasios! 💪 Una solución completa que combina gestión de usuarios, inventario, dietas, entrenamientos y ventas.",
      fullDescription: "En una única plataforma. Diseñada para optimizar la administración y mejorar la experiencia tanto de propietarios como de usuarios.",
      features: [
        "👥 Gestión de Usuarios: Control completo de membresías y accesos",
        "🥗 Sistema de Dietas: Creación y asignación de planes nutricionales personalizados",
        "💪 Gestión de Entrenamientos: Biblioteca completa de ejercicios y rutinas",
        "📦 Control de Inventario: Gestión de productos y suplementos",
        "💳 Sistema de Ventas Integrado: Procesamiento de pagos online y en persona"
      ],
      technologies: ["Flutter", "Firebase", "Firebase Authentication", "Cloud Firestore", "Firebase Storage"],
      image: "/images/projects/proyecto10.png",
      category: "mobile",
      status: "completed",
      links: {
        appStore: "https://apps.apple.com/at/app/eleven-private-sport-club/id6744098449"
      }
    },
    {
      id: 11,
      title: "Gestión Lila",
      subtitle: "",
      description: "💼 Gestión Lila – ¡Sistema integral de gestión para asociaciones y dispensarios cannábicos! 🚀 Una solución completa y moderna para la administración.",
      fullDescription: "De asociaciones y dispensarios cannábicos que integra gestión de socios, productos, ventas, informes y control de acceso en una plataforma robusta y escalable.",
      features: [
        "👥 Panel Principal Inteligente: Dashboard interactivo con métricas en tiempo real",
        "👤 Gestión de Socios: Control completo de usuarios y membresías",
        "📦 Sistema de Productos: Catálogo completo de productos con gestión de stock",
        "🏪 Gestión de Dispensario: Control de productos dispensados",
        "💰 Sistema de Ventas: Procesamiento eficiente de transacciones"
      ],
      technologies: ["Angular 17", "Material Design", "Node.js", "Express", "PostgreSQL", "Docker"],
      image: "/images/projects/proyecto11.png",
      category: "web",
      status: "completed",
      links: {
        demo: "https://r00tedbrain.github.io/GestionLilaAPP/"
      }
    },
    {
      id: 12,
      title: "DiscaMatch",
      subtitle: "",
      description: "💝 DiscaMatch – ¡Conectando corazones sin barreras! 🤝 Una plataforma inclusiva y segura diseñada específicamente para personas con diversidad funcional.",
      fullDescription: "Donde pueden encontrar amistad, amor y conexiones significativas en un entorno comprensivo y respetuoso.",
      features: [
        "🌈 Perfiles Inclusivos: Opciones personalizadas para describir tipos de diversidad funcional",
        "🤝 Match Inteligente: Algoritmo de compatibilidad basado en necesidades y preferencias similares",
        "💬 Comunicación Accesible: Chat con soporte para texto a voz y voz a texto",
        "🛡️ Seguridad y Privacidad: Verificación de perfiles para crear un entorno seguro",
        "♿ Accesibilidad Total: Diseño universal compatible con lectores de pantalla"
      ],
      technologies: ["Flutter", "Firebase", "Firebase Authentication", "Cloud Firestore", "Firebase Storage"],
      image: "/images/projects/proyecto12.png",
      category: "mobile",
      status: "completed",
      links: {
        demo: "https://r00tedbrain.github.io/adaptmatchwebsitev3/"
      }
    },
    {
      id: 13,
      title: "AlertaTelegram",
      subtitle: "Sistema de Emergencias Inteligente",
      description: "🚀 AlertaTelegram – Sistema de Emergencias Inteligente 🚨 Protección 24/7 con envío automático de ubicación, audio y fotos a tus contactos de confianza vía Telegram.",
      fullDescription: "Funciona en segundo plano para máxima seguridad.",
      features: [
        "🎤 Grabaciones de Audio: Envío automático cada 30 segundos en segundo plano",
        "📍 Ubicación en Tiempo Real: Actualización continua de posición GPS cada 10 segundos",
        "📷 Fotos de Emergencia: Captura automática de cámaras frontal y trasera cada 20 segundos",
        "📱 Mapa Interactivo: Visualización en tiempo real con pantalla completa",
        "🛡️ Funcionamiento en Background: Continúa funcionando aunque tu app esté cerrada"
      ],
      technologies: ["Flutter", "Telegram Bot API", "Geolocation", "Camera", "Background Services"],
      image: "/images/projects/alertateleg.png",
      category: "mobile",
      status: "completed"
    },
    {
      id: 14,
      title: "ShieldGuardAI",
      subtitle: "Generación Inteligente de Informes",
      description: "🚀 ShieldGuardAI – Generación Inteligente de Informes de Seguridad con IA 📋 Aplicación Angular especializada para ingenieros y arquitectos de España.",
      fullDescription: "Que automatiza la creación de informes de seguridad, planes de evacuación y documentación técnica profesional.",
      features: [
        "🤖 Inteligencia Artificial Avanzada: Procesamiento inteligente de datos empresariales en formato JSON",
        "📋 Planes de Evacuación: Creación automática de planes de evacuación profesionales",
        "🛡️ Planes de Seguridad: Elaboración de planes de seguridad integrales",
        "🚨 Planes de Respuesta a Incidencias: Protocolos de actuación ante emergencias",
        "🇪🇸 Especialización en Normativa Española: Cumplimiento con Real Decreto 393/2007"
      ],
      technologies: ["Angular", "Artificial Intelligence", "JSON Processing", "PDF Generation"],
      image: "/images/projects/shieldai.png",
      category: "web",
      status: "completed",
      links: {
        website: "https://shieldguardai.com/",
        demo: "https://r00tedbrain.github.io/ShieldGuardAi-DEMO/#/ShieldGuard"
      }
    },
    {
      id: 15,
      title: "Sherlock-OSINT",
      subtitle: "La App Definitiva de OSINT y Ciberseguridad",
      description: "🚀 Sherlock-OSINT – ¡La primera app móvil que reúne 16 herramientas profesionales de OSINT! Si eres pentester, investigador, profesional de ciberseguridad o simplemente quieres proteger tu privacidad digital, esta app es para ti.",
      fullDescription: "Primera aplicación móvil (iOS) y web que permite realizar investigaciones OSINT profesionales desde cualquier lugar. Con 16 herramientas integradas para analizar usuarios, emails, dominios, puertos, metadatos, bitcoin wallets y mucho más.",
      features: [
        "🔎 Username Analyzer: Busca perfiles en +400 plataformas sociales",
        "📧 Email Analyzer: Validación, reputación, análisis de seguridad",
        "📱 Phone Analyzer: Operador, país, tipo de línea",
        "🌐 Domain Analyzer: WHOIS, DNS, subdominios",
        "🔍 Port Scanner: Escaneo de puertos y servicios",
        "🔒 Shodan Search PRO: Dispositivos IoT y servidores",
        "🛡️ Reputation Checker: Verificación contra bases de amenazas",
        "💼 Company Intelligence: Geolocalización, tecnologías, contactos",
        "📋 BOE España: Búsqueda en Boletín Oficial del Estado",
        "📊 Metadata Extractor: EXIF, GPS, información de cámara",
        "🔐 Hash Analyzer: Identificación de tipos de hash",
        "📨 Email Forensics: Análisis de headers y routing",
        "₿ Bitcoin Analyzer: Análisis de wallets y transacciones",
        "🎯 Google Dorking: 50+ categorías automatizadas",
        "🗺️ Intelligence Map: Visualización estilo Maltego",
        "🔒 Privacidad extrema: Cero tracking, búsquedas anónimas"
      ],
      technologies: ["Flutter", "OSINT Framework", "Shodan API", "iOS", "Android", "Privacy First"],
      image: "/images/projects/sherlock-osint.png",
      category: "security",
      status: "completed",
      links: {
        appStore: "https://apps.apple.com/es/app/sherlock-osint/id6753150438",
        playStore: "https://play.google.com/store/apps/details?id=com.osint.osint_flutter"
      }
    },
    {
      id: 16,
      title: "TuAsistentedeFacturación",
      subtitle: "Gestión Inteligente para macOS",
      description: "🚀 TuAsistentedeFacturación – Gestión Inteligente de Facturación para macOS 💼 Asistente profesional desarrollado exclusivamente en Swift para Mac.",
      fullDescription: "Que simplifica la creación de facturas, presupuestos, gestión de gastos y administración de clientes.",
      features: [
        "📋 Creación de Facturas: Generación automática de facturas profesionales",
        "💰 Gestión de Presupuestos: Creación rápida de presupuestos detallados",
        "📊 Control de Gastos: Registro y categorización de gastos empresariales",
        "👥 Gestión de Clientes: Base de datos completa de clientes",
        "📈 Reportes y Análisis: Dashboard con métricas clave del negocio"
      ],
      technologies: ["Swift", "SwiftUI", "Core Data", "CloudKit", "PDFKit"],
      image: "/images/projects/asistente.png",
      category: "desktop",
      status: "completed",
      links: {
        appStore: "https://apps.apple.com/es/app/tuasistentedefacturación/id6743031327?mt=12"
      }
    },
    {
      id: 17,
      title: "CartasPicantes",
      subtitle: "Juego de Cartas para Parejas",
      description: "🔥 ¿Buscas una forma divertida y emocionante de conectar con tu pareja? CartasPicantes es el juego perfecto para parejas que quieren romper la rutina, descubrir nuevas facetas juntos y fortalecer su conexión. 💕",
      fullDescription: "Aplicación móvil desarrollada en Flutter y Firebase que ofrece un juego de cartas interactivo diseñado específicamente para parejas. Con más de 30 cartas diferentes, efectos de sonido y animaciones, CartasPicantes ayuda a las parejas a fortalecer su relación de manera divertida y emocionante.",
      features: [
        "🎮 Más de 30 cartas diferentes con retos y preguntas",
        "✨ Interfaz intuitiva y elegante",
        "🔊 Efectos de sonido y animaciones",
        "🔒 100% privado y seguro",
        "📴 Sin necesidad de conexión a internet para jugar",
        "💑 Diseñado para fortalecer la conexión de pareja",
        "🎯 Retos y preguntas para descubrir nuevas facetas",
        "🔥 Añade un toque picante a tus momentos especiales"
      ],
      technologies: ["Flutter", "Firebase", "Dart", "iOS", "Android"],
      image: "/images/projects/cartaspicantes.png",
      category: "mobile",
      status: "completed",
      links: {
        appStore: "https://apps.apple.com/es/app/cartaspicantes/id6752787153",
        playStore: "https://play.google.com/store/apps/details?id=com.marquez.parejas"
      }
    },
    {
      id: 18,
      title: "NarcoObservatorio",
      subtitle: "Noticias de Narcotráfico y Seguridad",
      description: "📰 NarcoObservatorio – Tu fuente confiable de información sobre narcotráfico, seguridad nacional y crimen organizado. Mantente informado con noticias actualizadas, análisis detallados y reportes especiales.",
      fullDescription: "Aplicación móvil desarrollada en Flutter que proporciona acceso a noticias actualizadas sobre narcotráfico, crimen organizado y seguridad pública. Con una interfaz intuitiva y categorización de contenido, NarcoObservatorio permite a usuarios interesados en seguridad y periodismo de investigación mantenerse al día con información verificada.",
      features: [
        "📱 Noticias en tiempo real sobre narcotráfico y seguridad",
        "🔍 Buscador avanzado de noticias",
        "📑 Categorización por temas: Narcotráfico, Operativos, Investigaciones",
        "🔔 Notificaciones push para noticias importantes",
        "💾 Guarda y comparte noticias de interés",
        "🌐 Navegador integrado con VPN privado",
        "📊 Estadísticas y análisis de tendencias",
        "🔒 Cumplimiento con RGPD y privacidad del usuario"
      ],
      technologies: ["Flutter", "Dart", "REST API", "Push Notifications", "iOS", "Android"],
      image: "/images/projects/narcoobservatorio.png",
      category: "mobile",
      status: "completed",
      links: {
        appStore: "https://apps.apple.com/es/app/narcoobservatorio/id6504436073"
      }
    }
  ];

  const categories = [
    { key: 'all', label: 'Todos' },
    { key: 'security', label: 'Seguridad' },
    { key: 'mobile', label: 'Móvil' },
    { key: 'web', label: 'Web' },
    { key: 'ai', label: 'IA/ML' },
    { key: 'desktop', label: 'Desktop' }
  ];

  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeCategory);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          <motion.h2 
            className="section-title neon-text animate-glow"
            variants={fadeInUp}
          >
            Proyectos Destacados
          </motion.h2>

          <motion.p 
            className="projects-subtitle"
            variants={fadeInUp}
          >
            Soluciones innovadoras que demuestran nuestra experiencia en desarrollo seguro, 
            tecnologías avanzadas y diseño centrado en el usuario.
          </motion.p>

          <motion.div 
            className="category-filters"
            variants={fadeInUp}
          >
            {categories.map((category) => (
              <button
                key={category.key}
                className={`filter-btn ${activeCategory === category.key ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.key)}
              >
                {category.label}
              </button>
            ))}
          </motion.div>

          <motion.div 
            className="projects-grid"
            variants={staggerContainer}
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="project-card glass-card"
                variants={fadeInUp}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay">
                    <span className={`project-status ${project.status}`}>
                      {project.status === 'completed' ? '✓ Completado' : '⚡ En Desarrollo'}
                    </span>
                  </div>
                </div>

                <div className="project-content">
                  <div className="project-header">
                    <h3 className="project-title">{project.title}</h3>
                    <p className="project-subtitle">{project.subtitle}</p>
                  </div>

                  <p className="project-description">{project.description}</p>

                  <div className="project-features">
                    <h4>Características Principales:</h4>
                    <ul>
                      {project.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="project-technologies">
                    <h4>Tecnologías:</h4>
                    <div className="tech-tags">
                      {project.technologies.map((tech, idx) => (
                        <span key={idx} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>

                  {project.links && (
                    <div className="project-links">
                      <h4>Enlaces:</h4>
                      <div className="project-buttons">
                        {project.links.playStore && (
                          <a 
                            href={project.links.playStore} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="project-link play-store"
                          >
                            📱 Play Store
                          </a>
                        )}
                        {project.links.appStore && (
                          <a 
                            href={project.links.appStore} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="project-link app-store"
                          >
                            🍎 App Store
                          </a>
                        )}
                        {project.links.fdroid && (
                          <a 
                            href={project.links.fdroid} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="project-link fdroid"
                          >
                            🤖 F-Droid
                          </a>
                        )}
                        {project.links.demo && (
                          <a 
                            href={project.links.demo} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="project-link demo"
                          >
                            🚀 Ver Demo
                          </a>
                        )}
                        {project.links.website && (
                          <a 
                            href={project.links.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="project-link website"
                          >
                            🌐 Sitio Web
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="projects-cta"
            variants={fadeInUp}
          >
            <h3 className="gradient-text">¿Tienes una idea innovadora?</h3>
            <p>Conversemos sobre cómo podemos convertir tu visión en una solución digital segura y escalable.</p>
            <button 
              className="btn-primary"
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Iniciar Proyecto
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
